// Grid navigation for every walking agent (the hero today, NPCs/bots later).
//
// One entry point — findRoute() — picks the best strategy per request:
//   'direct'  clear line of sight: walk straight, no search at all
//   'astar'   A* (octile heuristic, diagonal cost √2): the true shortest path
//   'partial' goal unreachable/too far: greedy walk to the closest reachable
//             point toward the goal instead of silently going nowhere
// Every result is string-pulled into the fewest straight segments, so agents
// move in smooth diagonals instead of tile staircases.

export interface NavGrid {
  cols: number;
  rows: number;
  /** true = the tile can NOT be walked (out of bounds counts as blocked). */
  blocked(tx: number, ty: number): boolean;
}

export interface Route {
  /** Waypoints in TILE coordinates, excluding the start tile. Empty = already there. */
  tiles: [number, number][];
  mode: 'direct' | 'astar' | 'partial';
  /** false = the route ends short of the goal (partial). */
  complete: boolean;
}

const SQRT2 = Math.SQRT2;
// 8 neighbours: [dx, dy, step cost]
const STEPS: [number, number, number][] = [
  [1, 0, 1], [-1, 0, 1], [0, 1, 1], [0, -1, 1],
  [1, 1, SQRT2], [1, -1, SQRT2], [-1, 1, SQRT2], [-1, -1, SQRT2],
];

/** Octile distance — admissible for 8-dir movement with √2 diagonals. */
const octile = (ax: number, ay: number, bx: number, by: number): number => {
  const dx = Math.abs(ax - bx), dy = Math.abs(ay - by);
  return Math.max(dx, dy) + (SQRT2 - 1) * Math.min(dx, dy);
};

/** Bresenham line over tiles: true if every tile on the segment is walkable. */
export function lineClear(g: NavGrid, ax: number, ay: number, bx: number, by: number): boolean {
  const dx = Math.abs(bx - ax), dy = Math.abs(by - ay);
  const sx = ax < bx ? 1 : -1, sy = ay < by ? 1 : -1;
  let x = ax, y = ay, err = dx - dy;
  for (;;) {
    if (g.blocked(x, y)) return false;
    if (x === bx && y === by) return true;
    const e2 = 2 * err;
    // On a diagonal step both orthogonal tiles must be clear too, or the agent
    // would clip the corner of a solid tile.
    if (e2 > -dy && e2 < dx) {
      if (g.blocked(x + sx, y) || g.blocked(x, y + sy)) return false;
      err -= dy; err += dx; x += sx; y += sy;
    } else if (e2 > -dy) { err -= dy; x += sx; }
    else { err += dx; y += sy; }
  }
}

/** Keep only the turn points that need a turn: greedy line-of-sight skipping. */
function stringPull(g: NavGrid, sx: number, sy: number, tiles: [number, number][]): [number, number][] {
  const out: [number, number][] = [];
  let ax = sx, ay = sy, i = 0;
  while (i < tiles.length) {
    let j = i;
    for (let k = tiles.length - 1; k > i; k--) {
      if (lineClear(g, ax, ay, tiles[k][0], tiles[k][1])) { j = k; break; }
    }
    out.push(tiles[j]);
    [ax, ay] = tiles[j];
    i = j + 1;
  }
  return out;
}

// ─── Binary min-heap keyed by f-score (flat arrays, no allocation per pop) ────
class Heap {
  private idx: number[] = [];
  private f: number[] = [];
  get size() { return this.idx.length; }
  push(i: number, fi: number) {
    this.idx.push(i); this.f.push(fi);
    let c = this.idx.length - 1;
    while (c > 0) {
      const p = (c - 1) >> 1;
      if (this.f[p] <= this.f[c]) break;
      this.swap(p, c); c = p;
    }
  }
  pop(): number {
    const top = this.idx[0], last = this.idx.length - 1;
    this.swap(0, last); this.idx.pop(); this.f.pop();
    let p = 0;
    for (;;) {
      const l = 2 * p + 1, r = l + 1;
      let m = p;
      if (l < this.f.length && this.f[l] < this.f[m]) m = l;
      if (r < this.f.length && this.f[r] < this.f[m]) m = r;
      if (m === p) break;
      this.swap(p, m); p = m;
    }
    return top;
  }
  private swap(a: number, b: number) {
    [this.idx[a], this.idx[b]] = [this.idx[b], this.idx[a]];
    [this.f[a], this.f[b]] = [this.f[b], this.f[a]];
  }
}

export interface RouteOptions {
  /** Max nodes A* may expand before giving up (default 12000). */
  maxExpand?: number;
}

/**
 * Route from start to goal (tile coords). Strategy is picked automatically:
 * direct line → A* shortest path → greedy partial (closest reachable point).
 * A blocked goal routes to the nearest open tile around it first.
 */
export function findRoute(g: NavGrid, sx: number, sy: number, gx: number, gy: number, opts: RouteOptions = {}): Route {
  const maxExpand = opts.maxExpand ?? 12_000;
  sx = clamp(sx, 0, g.cols - 1); sy = clamp(sy, 0, g.rows - 1);
  const open = g.blocked(gx, gy) ? nearestOpen(g, gx, gy) : ([gx, gy] as [number, number]);
  if (!open) return { tiles: [], mode: 'partial', complete: false };
  [gx, gy] = open;
  if (sx === gx && sy === gy) return { tiles: [], mode: 'direct', complete: true };

  // 1. Straight shot when nothing is in the way.
  if (!g.blocked(sx, sy) && lineClear(g, sx, sy, gx, gy)) {
    return { tiles: [[gx, gy]], mode: 'direct', complete: true };
  }

  // 2. A* with octile heuristic — optimal and fast on open maps.
  const { cols, rows } = g;
  const n = cols * rows;
  const gScore = new Float64Array(n).fill(Infinity);
  const prev = new Int32Array(n).fill(-1);
  const closed = new Uint8Array(n);
  const start = sy * cols + sx, goal = gy * cols + gx;
  const heap = new Heap();
  gScore[start] = 0;
  heap.push(start, octile(sx, sy, gx, gy));
  let expanded = 0;
  let best = start, bestH = octile(sx, sy, gx, gy);   // closest-approach node, for the partial fallback
  let reached = false;

  while (heap.size) {
    const cur = heap.pop();
    if (closed[cur]) continue;
    closed[cur] = 1;
    if (cur === goal) { reached = true; break; }
    if (++expanded > maxExpand) break;
    const cx = cur % cols, cy = (cur - cx) / cols;
    for (const [dx, dy, cost] of STEPS) {
      const nx = cx + dx, ny = cy + dy;
      if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue;
      const ni = ny * cols + nx;
      if (closed[ni] || g.blocked(nx, ny)) continue;
      if (dx && dy && (g.blocked(cx + dx, cy) || g.blocked(cx, cy + dy))) continue; // no corner cutting
      const ng = gScore[cur] + cost;
      if (ng >= gScore[ni]) continue;
      gScore[ni] = ng; prev[ni] = cur;
      const h = octile(nx, ny, gx, gy);
      if (h < bestH) { bestH = h; best = ni; }
      heap.push(ni, ng + h);
    }
  }

  // 3. Unreachable or out of budget: walk to the closest point we DID reach.
  const end = reached ? goal : best;
  if (end === start) return { tiles: [], mode: 'partial', complete: false };
  const tiles: [number, number][] = [];
  for (let c = end; c !== start; c = prev[c]) tiles.push([c % cols, (c - (c % cols)) / cols]);
  tiles.reverse();
  return {
    tiles: stringPull(g, sx, sy, tiles),
    mode: reached ? 'astar' : 'partial',
    complete: reached,
  };
}

/** Nearest walkable tile to (tx,ty), spiralling outwards (for clicks onto solids). */
export function nearestOpen(g: NavGrid, tx: number, ty: number, maxR = 6): [number, number] | null {
  if (!g.blocked(tx, ty)) return [tx, ty];
  for (let r = 1; r <= maxR; r++) {
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.abs(dx) !== r && Math.abs(dy) !== r) continue;
        if (!g.blocked(tx + dx, ty + dy)) return [tx + dx, ty + dy];
      }
    }
  }
  return null;
}

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
